import type { BoxAnswer, BoxConfirmationRequiredBody } from "../api/shopMode";
import { t } from "../i18n/t";

/**
 * G-84 (OD-47) — the client half of "Is this from box X?", kept pure so it is
 * testable without rendering (the house rule for kiosk logic).
 *
 * The server refuses a consumption that would touch a box nobody answered for
 * with a 409 whose body carries `code: BOX_CONFIRMATION_REQUIRED` and the
 * boxes involved. The surface asks, merges the answers into the request, and
 * sends it again; a second box can surface on the second attempt, so answers
 * ACCUMULATE across the loop rather than replacing each other.
 */
export function parseBoxConfirmation(err: unknown): BoxConfirmationRequiredBody | null {
  if (typeof err !== "object" || err === null) return null;
  const status = (err as { status?: unknown }).status;
  const body = (err as { body?: unknown }).body;
  if (status !== 409 || typeof body !== "object" || body === null) return null;
  const b = body as { code?: unknown; message?: unknown; details?: unknown };
  if (b.code !== "BOX_CONFIRMATION_REQUIRED") return null;
  // The boxes ride under `details` — the only structured payload the server's
  // exception filter passes through. Read nothing else: a body that lost them
  // is a server the client cannot ask a question for, and pretending
  // otherwise would render an empty prompt.
  const details = b.details;
  const boxes =
    details && typeof details === "object" && Array.isArray((details as { boxes?: unknown }).boxes)
      ? ((details as { boxes: BoxConfirmationRequiredBody["boxes"] }).boxes)
      : null;
  if (!boxes || boxes.length === 0) return null;
  return {
    code: "BOX_CONFIRMATION_REQUIRED",
    message: typeof b.message === "string" ? b.message : "",
    boxes,
  };
}

/** Later answers win for the same box; boxes only answered once are kept. */
export function mergeBoxAnswers(prev: BoxAnswer[], next: BoxAnswer[]): BoxAnswer[] {
  const byBox = new Map(prev.map((a) => [a.boxId, a]));
  for (const a of next) byBox.set(a.boxId, a);
  return [...byBox.values()];
}

/**
 * Runs a consuming request, asking whenever the server names a box, until it
 * goes through or the person backs out. `ask` resolves with the answers, or
 * null when they cancelled. Bounded by the number of boxes the server can
 * name — every loop adds at least one answered box, and the server never
 * asks about an answered one again.
 */
export async function runWithBoxAnswers<T>(
  call: (answers: BoxAnswer[]) => Promise<T>,
  ask: (boxes: BoxConfirmationRequiredBody["boxes"]) => Promise<BoxAnswer[] | null>,
  maxRounds = 20,
): Promise<T> {
  let answers: BoxAnswer[] = [];
  for (let round = 0; round < maxRounds; round += 1) {
    try {
      return await call(answers);
    } catch (err: unknown) {
      const needed = parseBoxConfirmation(err);
      if (!needed) throw err;
      const given = await ask(needed.boxes);
      if (!given) throw new BoxPromptCancelled();
      answers = mergeBoxAnswers(answers, given);
    }
  }
  throw new Error(t("box.tooManyToConfirm"));
}

export class BoxPromptCancelled extends Error {
  constructor() {
    // Evaluated at construction, not at import — the words are right for
    // whatever language the counter is showing when the florist backs out.
    super(t("box.cancelledNothingRecorded"));
    this.name = "BoxPromptCancelled";
  }
}
